import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL;
const MODES = [["default", "Device default"], ["sts", "Speech-to-speech"], ["cascade", "Cascade"]];
const CASCADE_ROLES = [["stt", "Speech recognition"], ["llm", "Language model"], ["tts", "Voice"]];

async function getJson(path) {
  const response = await fetch(`${API}${path}`, { credentials: "include" });
  if (!response.ok) throw new Error("Could not load the voice models.");
  return response.json();
}

// Voice model of one device, stored in its resident's device settings (the
// device picks it up within about a minute). The catalog and its key check
// live in backend/app/services/voice_models.py.
export default function VoiceModelModal({ device, onClose }) {
  const [catalog, setCatalog] = useState(null);
  const [model, setModel] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const patientId = device?.patient_id;
  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setCatalog(null);
    setError("");
    Promise.all([getJson("/admin/voice-models"), getJson(`/patients/${patientId}/device-settings`)])
      .then(([models, settings]) => {
        if (cancelled) return;
        setCatalog(models);
        setModel(settings.model);
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [patientId]);
  if (!device) return null;

  const mode = model?.mode || "default";
  // Catalog order puts the device's code defaults first.
  const first = (role) => (catalog[role].find((m) => m.available) || catalog[role][0]).id;
  function choose(next) {
    if (next === "default") return setModel(null);
    const roles = next === "sts" ? ["sts"] : CASCADE_ROLES.map(([role]) => role);
    setModel({ mode: next, ...Object.fromEntries(roles.map((role) => [role, model?.[role] || first(role)])) });
  }
  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      // Fresh copy, so quiet hours or alarms edited meanwhile in the apps are kept.
      const settings = await getJson(`/patients/${patientId}/device-settings`);
      const response = await fetch(`${API}/patients/${patientId}/device-settings`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...settings, model }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save the voice model.");
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }
  const field = "w-full rounded-lg border border-gray-300 px-3 py-2";
  const select = (role, label) => (
    <label key={role} className="block">{label}
      <select className={field} value={model[role]} onChange={(e) => setModel({ ...model, [role]: e.target.value })}>
        {catalog[role].map((m) => <option key={m.id} value={m.id} disabled={!m.available}>{m.label}{m.available ? "" : " (key missing)"}</option>)}
      </select>
    </label>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <form onSubmit={save} className="w-full max-w-xl space-y-4 rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-2xl font-semibold">Voice model · {device.public_id}</h2>
        <p className="text-sm text-gray-600">{device.patient_name}. Applies from the next conversation, about a minute after saving.</p>
        {!catalog ? !error && <p>Loading…</p> : <>
          <div role="radiogroup" aria-label="Mode" className="grid grid-cols-3 gap-2">
            {MODES.map(([value, label]) => (
              <button key={value} type="button" role="radio" aria-checked={mode === value} onClick={() => choose(value)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${mode === value ? "border-[#76B3FF] bg-[#76B3FF] text-white" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
                {label}
              </button>
            ))}
          </div>
          {mode === "default" && <p className="text-sm text-gray-600">The device uses the models set in its .env.</p>}
          {mode === "sts" && select("sts", "Model")}
          {mode === "cascade" && CASCADE_ROLES.map(([role, label]) => select(role, label))}
        </>}
        {error && <p role="alert" className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" disabled={saving} onClick={onClose} className="rounded-lg border px-4 py-2">Cancel</button>
          <button disabled={!catalog || saving} className="rounded-lg bg-blue-500 px-4 py-2 text-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
